import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import type {
  CommunitySpace,
  PostWithAuthor,
  PostWithContext,
  CommentWithAuthor,
  CommunityVote,
  CommunityUserProfile,
  UserNotification,
  CommunitySearchResult,
  SortMode,
  TimePeriod,
  ReportReason,
  NotificationPreferences,
  NotificationPrefKey,
} from '@/types/community';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@/types/community';
import { extractMentionsFromTipTap, diffMentions, extractTextFromTipTap } from '@/types/community';
import type { Json } from '@/types/database.generated';

// Community tables are not yet in database.generated.ts (added via direct migration).
// Use untyped .from() calls until types are regenerated.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Query Keys ──────────────────────────────────────────────────────────────

export const communityKeys = {
  all: ['community'] as const,
  spaces: () => [...communityKeys.all, 'spaces'] as const,
  posts: (spaceSlug: string, sort: SortMode, timePeriod?: TimePeriod, subjects?: string[], tags?: string[]) =>
    [...communityKeys.all, 'posts', spaceSlug, sort, timePeriod, subjects, tags] as const,
  activityFeed: (subjects?: string[], tags?: string[]) =>
    [...communityKeys.all, 'activity-feed', subjects, tags] as const,
  post: (id: string) => [...communityKeys.all, 'post', id] as const,
  comments: (postId: string) => [...communityKeys.all, 'comments', postId] as const,
  votes: (postId: string) => [...communityKeys.all, 'votes', postId] as const,
  bookmarks: () => [...communityKeys.all, 'bookmarks'] as const,
  profile: () => [...communityKeys.all, 'profile'] as const,
  lawSchools: () => [...communityKeys.all, 'law-schools'] as const,
  notifications: () => [...communityKeys.all, 'notifications'] as const,
  userProfile: (userId: string) => [...communityKeys.all, 'user-profile', userId] as const,
  userPosts: (userId: string) => [...communityKeys.all, 'user-posts', userId] as const,
  search: (query: string, spaceSlug?: string) => [...communityKeys.all, 'search', query, spaceSlug] as const,
  notificationPrefs: () => [...communityKeys.all, 'notification-prefs'] as const,
  startingPoint: (postId: string) => [...communityKeys.all, 'starting-point', postId] as const,
};

// PostgREST join select for posts with author
// Uses the direct FK community_posts_author_profile_fkey (added in postgrest_fks migration)
const POST_WITH_AUTHOR_SELECT = `
  *,
  author:community_user_profiles!community_posts_author_profile_fkey(
    user_id, display_name, badge, bio, is_banned
  )
`;

// PostgREST join select for posts with author + space
const POST_WITH_CONTEXT_SELECT = `
  *,
  author:community_user_profiles!community_posts_author_profile_fkey(
    user_id, display_name, badge, bio, is_banned
  ),
  space:community_spaces!community_posts_space_id_fkey(
    slug, name
  )
`;

const COMMENT_WITH_AUTHOR_SELECT = `
  *,
  author:community_user_profiles!community_comments_author_profile_fkey(
    user_id, display_name, badge, bio, is_banned
  )
`;

const PAGE_SIZE = 20;

// ── Space Hooks ─────────────────────────────────────────────────────────────

export function useCommunitySpaces() {
  return useQuery<CommunitySpace[]>({
    queryKey: communityKeys.spaces(),
    queryFn: async () => {
      const { data, error } = await db
        .from('community_spaces')
        .select('*')
        .eq('is_archived', false)
        .order('sort_order');

      if (error) throw error;
      return data as CommunitySpace[];
    },
    staleTime: 5 * 60 * 1000, // spaces rarely change
  });
}

// ── Post Hooks ──────────────────────────────────────────────────────────────

/** Fetch paginated posts for a space with sorting */
export function useCommunityPosts(
  spaceSlug: string | undefined,
  sort: SortMode = 'new',
  timePeriod: TimePeriod = 'all',
  subjects?: string[],
  tags?: string[],
) {
  return useInfiniteQuery<PostWithAuthor[]>({
    queryKey: communityKeys.posts(spaceSlug || '', sort, timePeriod, subjects, tags),
    queryFn: async ({ pageParam }) => {
      // First resolve slug to space ID
      let query = db
        .from('community_posts')
        .select(POST_WITH_AUTHOR_SELECT)
        .is('deleted_at', null);

      if (spaceSlug) {
        // Get space ID from slug
        const { data: space } = await db
          .from('community_spaces')
          .select('id')
          .eq('slug', spaceSlug)
          .single();
        if (space) {
          query = query.eq('space_id', space.id);
        }
      }

      // Subject / tag filters
      if (subjects?.length) {
        query = query.in('subject_id', subjects);
      }
      if (tags?.length) {
        query = query.overlaps('tags', tags);
      }

      // Time period filter for "top" sort
      if (sort === 'top' && timePeriod !== 'all') {
        const now = new Date();
        let since: Date;
        switch (timePeriod) {
          case 'today': since = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
          case 'week': since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
          case 'month': since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
          default: since = new Date(0);
        }
        query = query.gte('created_at', since.toISOString());
      }

      // Sort order
      if (sort === 'new') {
        query = query.order('created_at', { ascending: false });
      } else {
        // 'top' sorts by vote_count descending
        query = query.order('vote_count', { ascending: false });
      }

      // Cursor-based pagination
      if (pageParam) {
        query = query.lt('created_at', pageParam as string);
      }

      query = query.limit(PAGE_SIZE);

      const { data, error } = await query;
      if (error) throw error;

      return data as PostWithAuthor[];
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1]?.created_at;
    },
    enabled: !!spaceSlug,
    staleTime: 30 * 1000,
  });
}

/** Fetch recent posts across all spaces for landing page */
export function useActivityFeed(subjects?: string[], tags?: string[]) {
  return useInfiniteQuery<PostWithContext[]>({
    queryKey: communityKeys.activityFeed(subjects, tags),
    queryFn: async ({ pageParam }) => {
      let query = db
        .from('community_posts')
        .select(POST_WITH_CONTEXT_SELECT)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (subjects?.length) {
        query = query.in('subject_id', subjects);
      }
      if (tags?.length) {
        query = query.overlaps('tags', tags);
      }

      if (pageParam) {
        query = query.lt('created_at', pageParam as string);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PostWithContext[];
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1]?.created_at;
    },
    staleTime: 30 * 1000,
  });
}

/** Fetch a single post by ID */
export function useCommunityPost(postId: string | undefined) {
  return useQuery<PostWithAuthor | null>({
    queryKey: communityKeys.post(postId || ''),
    queryFn: async () => {
      if (!postId) return null;
      const { data, error } = await db
        .from('community_posts')
        .select(POST_WITH_AUTHOR_SELECT)
        .eq('id', postId)
        .single();

      if (error) throw error;
      return data as PostWithAuthor;
    },
    enabled: !!postId,
    staleTime: 30 * 1000,
  });
}

// ── Comment Hooks ───────────────────────────────────────────────────────────

/** Fetch all comments for a post (flat list — tree built client-side) */
export function useCommunityComments(postId: string | undefined) {
  return useQuery<CommentWithAuthor[]>({
    queryKey: communityKeys.comments(postId || ''),
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await db
        .from('community_comments')
        .select(COMMENT_WITH_AUTHOR_SELECT)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as CommentWithAuthor[];
    },
    enabled: !!postId,
    staleTime: 30 * 1000,
  });
}

// ── Vote Hooks ──────────────────────────────────────────────────────────────

/** Batch-fetch user's vote status for multiple posts (eliminates N+1 in list views) */
export function useUserPostVotes(postIds: string[]) {
  const { user } = useAuth();
  const stableKey = postIds.slice().sort().join(',');
  return useQuery<Set<string>>({
    queryKey: [...communityKeys.all, 'post-votes', stableKey],
    queryFn: async () => {
      if (!user || postIds.length === 0) return new Set<string>();
      const { data, error } = await db
        .from('community_votes')
        .select('post_id')
        .eq('user_id', user.id)
        .in('post_id', postIds)
        .not('post_id', 'is', null);
      if (error) throw error;
      return new Set((data || []).map((v: { post_id: string }) => v.post_id));
    },
    enabled: !!user && postIds.length > 0,
    staleTime: 60 * 1000,
    structuralSharing: false,
  });
}

/** Fetch user's votes for a post and its comments (used on PostDetail page) */
export function useUserVotes(postId: string | undefined) {
  const { user } = useAuth();
  return useQuery<CommunityVote[]>({
    queryKey: communityKeys.votes(postId || ''),
    queryFn: async () => {
      if (!postId || !user) return [];

      // Fetch post vote + comment votes for this post in one go
      // Post vote
      const { data: postVotes, error: pvError } = await db
        .from('community_votes')
        .select('*')
        .eq('user_id', user.id)
        .eq('post_id', postId);
      if (pvError) throw pvError;

      // Comment votes for this post's comments
      const { data: comments } = await db
        .from('community_comments')
        .select('id')
        .eq('post_id', postId);
      const commentIds = (comments || []).map((c: { id: string }) => c.id);

      let commentVotes: CommunityVote[] = [];
      if (commentIds.length > 0) {
        const { data: cv, error: cvError } = await db
          .from('community_votes')
          .select('*')
          .eq('user_id', user.id)
          .in('comment_id', commentIds);
        if (cvError) throw cvError;
        commentVotes = (cv || []) as CommunityVote[];
      }

      return [...(postVotes || []), ...commentVotes] as CommunityVote[];
    },
    enabled: !!postId && !!user,
    staleTime: 60 * 1000,
  });
}

// ── Bookmark Hooks ──────────────────────────────────────────────────────────

/** Fetch user's bookmarked post IDs */
export function useUserBookmarks() {
  const { user } = useAuth();
  return useQuery<Set<string>>({
    queryKey: communityKeys.bookmarks(),
    queryFn: async () => {
      if (!user) return new Set<string>();
      const { data, error } = await db
        .from('community_bookmarks')
        .select('post_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return new Set((data || []).map((b: { post_id: string }) => b.post_id));
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    structuralSharing: false,
  });
}

// ── Profile Hooks ───────────────────────────────────────────────────────────

/** Fetch current user's community profile */
export function useCommunityProfile() {
  const { user } = useAuth();
  return useQuery<CommunityUserProfile | null>({
    queryKey: communityKeys.profile(),
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await db
        .from('community_user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data as CommunityUserProfile | null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Auto-creates a community profile if the user doesn't have one yet.
 * Generates a random anonymous display name (e.g., "bravescholar4271").
 * Users can change it later from Settings > Community Settings.
 * Call early in the community page tree.
 * Returns { profile, isLoading } — profile is null while loading or if unauthenticated.
 */
export function useEnsureCommunityProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const profileQuery = useCommunityProfile();

  const createProfile = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { generateRandomDisplayName } = await import('@/lib/random-display-name');
      const displayName = generateRandomDisplayName();

      const { data, error } = await db
        .from('community_user_profiles')
        .insert({ user_id: user.id, display_name: displayName })
        .select('*')
        .single();
      if (error) throw error;
      return data as CommunityUserProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(communityKeys.profile(), data);
    },
  });

  // Auto-create if authenticated, profile loaded, and no profile exists
  const shouldCreate = !!user && !profileQuery.isLoading && profileQuery.data === null && !createProfile.isPending && !createProfile.isSuccess;
  useEffect(() => {
    if (shouldCreate) createProfile.mutate();
  }, [shouldCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const profile = profileQuery.data ?? createProfile.data ?? null;

  return {
    profile,
    isLoading: profileQuery.isLoading || createProfile.isPending,
    needsOnboarding: !!profile && !profile.onboarded_at,
  };
}

// ── Law Schools ─────────────────────────────────────────────────────────────

interface LawSchool {
  id: string;
  name: string;
  city: string;
  state: string;
}

/** Fetch all law schools (static data, cached forever) */
export function useLawSchools() {
  return useQuery<LawSchool[]>({
    queryKey: communityKeys.lawSchools(),
    queryFn: async () => {
      const { data, error } = await db
        .from('law_schools')
        .select('id, name, city, state')
        .order('name');
      if (error) throw error;
      return data as LawSchool[];
    },
    staleTime: Infinity,
  });
}

// ── Community Profile Update ────────────────────────────────────────────────

/** Update current user's community profile (display name, visibility toggles) */
export function useUpdateCommunityProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: {
      display_name?: string;
      badge?: string | null;
      show_school?: boolean;
      show_real_name_in_groups?: boolean;
      onboarded_at?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await db
        .from('community_user_profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as CommunityUserProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(communityKeys.profile(), data);
      // Invalidate posts/comments so updated display name shows everywhere
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/** Check if a display name is available (debounced, case-insensitive) */
export function useCheckDisplayNameAvailability(name: string, currentName: string) {
  const { user } = useAuth();
  const trimmed = name.trim();
  const debouncedName = useDebouncedValue(trimmed, 500);
  const isSameAsCurrent = debouncedName.toLowerCase() === currentName.toLowerCase();
  const shouldCheck = !!user && debouncedName.length >= 2 && !isSameAsCurrent;

  const query = useQuery<boolean>({
    queryKey: [...communityKeys.all, 'display-name-check', debouncedName.toLowerCase()],
    queryFn: async () => {
      // Escape LIKE wildcards (%, _, \) to prevent pattern matching
      const escaped = debouncedName.replace(/[%_\\]/g, '\\$&');
      const { data, error } = await db
        .from('community_user_profiles')
        .select('user_id')
        .ilike('display_name', escaped)
        .limit(1);
      if (error) throw error;
      // Available if no rows, or only row is the current user
      return !data?.length || data.every((r: { user_id: string }) => r.user_id === user!.id);
    },
    enabled: shouldCheck,
    staleTime: 10_000,
    gcTime: 30_000,
  });

  return {
    isAvailable: shouldCheck ? (query.data ?? null) : null,
    isChecking: shouldCheck && query.isFetching,
  };
}

/** Update current user's school on the profiles table */
export function useUpdateProfileSchool() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (schoolId: string | null) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db
        .from('profiles')
        .update({ school_id: schoolId })
        .eq('id', user.id);
      if (error) throw error;
    },
  });
}

// ── Mention + Notification Helpers ───────────────────────────────────────

/** Persist mentions to community_mentions and create notifications */
async function createMentionsAndNotify(opts: {
  userId: string;
  displayName: string;
  mentionedUserIds: string[];
  sourceType: 'post' | 'comment';
  sourceId: string;
  postId: string;
  postTitle: string;
  body: Json | null;
}) {
  const { userId, displayName, mentionedUserIds, sourceType, sourceId, postId, postTitle, body } = opts;

  // Filter out self-mentions
  const others = mentionedUserIds.filter((id) => id !== userId);
  if (others.length === 0) return;

  const deepLink = sourceType === 'comment'
    ? `/community/post/${postId}#comment-${sourceId}`
    : `/community/post/${postId}`;

  // Insert mentions and notifications independently — one failing shouldn't block the other
  const mentionRows = others.map((uid) => ({
    mentioner_id: userId,
    mentioned_user_id: uid,
    source_type: sourceType,
    source_id: sourceId,
  }));

  const notifRows = others.map((uid) => ({
    user_id: uid,
    type: 'mention' as const,
    actor_id: userId,
    source_type: sourceType,
    source_id: sourceId,
    title: `@${displayName} mentioned you in "${postTitle}"`,
    body: extractTextFromTipTap(body, 100),
    deep_link: deepLink,
  }));

  const results = await Promise.allSettled([
    db.from('community_mentions').insert(mentionRows),
    db.from('user_notifications').insert(notifRows),
  ]);
  for (const r of results) {
    if (r.status === 'rejected') console.error('Mention/notification insert failed:', r.reason);
    else if (r.value?.error) console.error('Mention/notification insert error:', r.value.error);
  }
}

// ── Mutations ───────────────────────────────────────────────────────────────

/** Create a new post */
export function useCreatePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (post: {
      space_id: string;
      title: string;
      body: unknown;
      subject_id?: string | null;
      tags?: string[];
      /** Author display name — needed for mention notifications */
      displayName?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { displayName, ...insertData } = post;
      const { data, error } = await db
        .from('community_posts')
        .insert({
          ...insertData,
          author_id: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;

      // Create mentions + notifications (fire-and-forget, don't block post creation)
      const mentioned = extractMentionsFromTipTap(post.body as Json | null);
      if (mentioned.length > 0 && displayName) {
        createMentionsAndNotify({
          userId: user.id,
          displayName,
          mentionedUserIds: mentioned,
          sourceType: 'post',
          sourceId: (data as { id: string }).id,
          postId: (data as { id: string }).id,
          postTitle: post.title,
          body: post.body as Json | null,
        }).catch((err) => console.error('Failed to create mention notifications:', err));
      }

      return data as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/** Update a post (edit within 24h) */
export function useUpdatePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, oldBody, displayName, ...updates }: {
      id: string;
      title?: string;
      body?: unknown;
      subject_id?: string | null;
      tags?: string[];
      /** Previous body for mention diffing */
      oldBody?: unknown;
      /** Author display name for notifications */
      displayName?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db
        .from('community_posts')
        .update({ ...updates, edited_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;

      // Only notify for NEWLY added mentions
      if (updates.body && oldBody && displayName) {
        const { added } = diffMentions(oldBody as Json | null, updates.body as Json | null);
        if (added.length > 0) {
          createMentionsAndNotify({
            userId: user.id,
            displayName,
            mentionedUserIds: added,
            sourceType: 'post',
            sourceId: id,
            postId: id,
            postTitle: updates.title || (queryClient.getQueryData<PostWithAuthor>(communityKeys.post(id)))?.title || 'a post',
            body: updates.body as Json | null,
          }).catch((err) => console.error('Failed to create mention notifications:', err));
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.id) });
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/** Soft-delete a post */
export function useDeletePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (postId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db
        .from('community_posts')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: user.id,
        })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/** Create a comment or reply */
export function useCreateComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (comment: {
      post_id: string;
      parent_comment_id?: string | null;
      body: unknown;
      /** Context for mention notifications */
      displayName?: string;
      postTitle?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { displayName, postTitle, ...insertData } = comment;
      const { data, error } = await db
        .from('community_comments')
        .insert({
          ...insertData,
          author_id: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;

      const commentId = (data as { id: string }).id;
      const mentionedUserIds = extractMentionsFromTipTap(comment.body as Json | null);
      const mentionedSet = new Set(mentionedUserIds);

      // Create mentions + notifications
      if (mentionedUserIds.length > 0 && displayName && postTitle) {
        createMentionsAndNotify({
          userId: user.id,
          displayName,
          mentionedUserIds,
          sourceType: 'comment',
          sourceId: commentId,
          postId: comment.post_id,
          postTitle,
          body: comment.body as Json | null,
        }).catch((err) => console.error('Failed to create mention notifications:', err));
      }

      // Reply notifications (fire-and-forget, don't block the mutation)
      const snippet = extractTextFromTipTap(comment.body as Json | null).slice(0, 120);
      const deepLink = `/community/post/${comment.post_id}`;
      const replyNotifs: Array<{
        user_id: string; type: string; title: string; body: string; deep_link: string;
      }> = [];

      try {
        // Notify post author for reply_to_post (skip self, skip if already mentioned)
        if (comment.parent_comment_id) {
          // Reply to a comment — notify parent comment author
          const { data: parentComment } = await db
            .from('community_comments')
            .select('author_id')
            .eq('id', comment.parent_comment_id)
            .single();
          if (parentComment?.author_id && parentComment.author_id !== user.id && !mentionedSet.has(parentComment.author_id)) {
            replyNotifs.push({
              user_id: parentComment.author_id,
              type: 'reply_to_comment',
              title: `${displayName || 'Someone'} replied to your comment`,
              body: snippet,
              deep_link: deepLink,
            });
          }
        }

        // Always notify post author of any new comment (unless self or already mentioned or already getting reply_to_comment)
        const { data: postData } = await db
          .from('community_posts')
          .select('author_id')
          .eq('id', comment.post_id)
          .single();
        const alreadyNotified = new Set(replyNotifs.map(n => n.user_id));
        if (postData?.author_id && postData.author_id !== user.id && !mentionedSet.has(postData.author_id) && !alreadyNotified.has(postData.author_id)) {
          replyNotifs.push({
            user_id: postData.author_id,
            type: 'reply_to_post',
            title: `${displayName || 'Someone'} commented on "${postTitle || 'your post'}"`,
            body: snippet,
            deep_link: deepLink,
          });
        }

        if (replyNotifs.length > 0) {
          await db.from('user_notifications').insert(replyNotifs);
        }
      } catch (err) {
        console.error('Failed to create reply notifications:', err);
      }

      return data as { id: string };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.comments(variables.post_id) });
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.post_id) });
    },
  });
}

/** Update a comment (edit) */
export function useUpdateComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, postId, body, oldBody, displayName, postTitle }: {
      id: string;
      postId: string;
      body: unknown;
      /** Previous body for mention diffing */
      oldBody?: unknown;
      /** Author display name for notifications */
      displayName?: string;
      /** Post title for notification text */
      postTitle?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db
        .from('community_comments')
        .update({ body: body as Json, edited_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;

      // Only notify for NEWLY added mentions
      if (oldBody && displayName && postTitle) {
        const { added } = diffMentions(oldBody as Json | null, body as Json | null);
        if (added.length > 0) {
          createMentionsAndNotify({
            userId: user.id,
            displayName,
            mentionedUserIds: added,
            sourceType: 'comment',
            sourceId: id,
            postId,
            postTitle,
            body: body as Json | null,
          }).catch((err) => console.error('Failed to create mention notifications:', err));
        }
      }

      return { postId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.comments(result.postId) });
    },
  });
}

/** Soft-delete a comment */
export function useDeleteComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, postId }: { id: string; postId: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db
        .from('community_comments')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: user.id,
        })
        .eq('id', id);
      if (error) throw error;
      return { postId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.comments(result.postId) });
    },
  });
}

/** Toggle vote on a post or comment (optimistic) */
export function useToggleVote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      postId,
      commentId,
      hasVoted,
    }: {
      postId?: string;
      commentId?: string;
      hasVoted: boolean;
      /** The post ID for cache invalidation (always needed) */
      contextPostId: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      if (hasVoted) {
        // Remove vote
        let query = db.from('community_votes').delete().eq('user_id', user.id);
        if (postId) query = query.eq('post_id', postId);
        if (commentId) query = query.eq('comment_id', commentId);
        const { error } = await query;
        if (error) throw error;
      } else {
        // Add vote
        const { error } = await db.from('community_votes').insert({
          user_id: user.id,
          post_id: postId || null,
          comment_id: commentId || null,
        });
        if (error) throw error;
      }
    },
    // Optimistic update — immediately flip vote state + vote count
    onMutate: async (variables) => {
      const votesKey = communityKeys.votes(variables.contextPostId);
      await queryClient.cancelQueries({ queryKey: votesKey });
      const previousVotes = queryClient.getQueryData<CommunityVote[]>(votesKey);

      // Optimistically update the votes array
      if (previousVotes) {
        let newVotes: CommunityVote[];
        if (variables.hasVoted) {
          newVotes = previousVotes.filter(v => {
            if (variables.postId) return v.post_id !== variables.postId;
            if (variables.commentId) return v.comment_id !== variables.commentId;
            return true;
          });
        } else {
          newVotes = [...previousVotes, {
            id: `optimistic-${Date.now()}`,
            user_id: '',
            post_id: variables.postId || null,
            comment_id: variables.commentId || null,
            created_at: new Date().toISOString(),
          }];
        }
        queryClient.setQueryData(votesKey, newVotes);
      }

      // Optimistically update the batch post-votes Set (for list views)
      const batchKeys = queryClient.getQueryCache().findAll({
        queryKey: [...communityKeys.all, 'post-votes'],
      });
      const prevBatchSnapshots: { key: readonly unknown[]; data: Set<string> }[] = [];
      if (variables.postId) {
        for (const query of batchKeys) {
          const prev = query.state.data as Set<string> | undefined;
          if (prev) {
            prevBatchSnapshots.push({ key: query.queryKey, data: new Set(prev) });
            const next = new Set(prev);
            if (variables.hasVoted) next.delete(variables.postId);
            else next.add(variables.postId);
            queryClient.setQueryData(query.queryKey, next);
          }
        }
      }

      return { previousVotes, votesKey, prevBatchSnapshots };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousVotes !== undefined) {
        queryClient.setQueryData(context.votesKey, context.previousVotes);
      }
      // Roll back batch snapshots
      for (const snap of context?.prevBatchSnapshots || []) {
        queryClient.setQueryData(snap.key, snap.data);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.votes(variables.contextPostId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.contextPostId) });
      // Invalidate both post lists and batch vote caches
      queryClient.invalidateQueries({ queryKey: [...communityKeys.all, 'posts'] });
      queryClient.invalidateQueries({ queryKey: [...communityKeys.all, 'post-votes'] });
    },
  });
}

/** Toggle bookmark on a post (optimistic) */
export function useToggleBookmark() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ postId, isBookmarked }: { postId: string; isBookmarked: boolean }) => {
      if (!user) throw new Error('Not authenticated');

      if (isBookmarked) {
        const { error } = await db
          .from('community_bookmarks')
          .delete()
          .eq('user_id', user.id)
          .eq('post_id', postId);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('community_bookmarks')
          .insert({ user_id: user.id, post_id: postId });
        if (error) throw error;
      }
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: communityKeys.bookmarks() });
      const prev = queryClient.getQueryData<Set<string>>(communityKeys.bookmarks());
      const next = new Set(prev);
      if (variables.isBookmarked) {
        next.delete(variables.postId);
      } else {
        next.add(variables.postId);
      }
      queryClient.setQueryData(communityKeys.bookmarks(), next);
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(communityKeys.bookmarks(), context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.bookmarks() });
    },
  });
}

/** Submit a report */
export function useCreateReport() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (report: {
      post_id?: string | null;
      comment_id?: string | null;
      reason: ReportReason;
      details?: string | null;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db.from('community_reports').insert({
        ...report,
        reporter_id: user.id,
      });
      if (error) throw error;
    },
  });
}

// ── User Profile Hooks (public profiles) ─────────────────────────────────

/** Fetch another user's community profile (public) */
export function useCommunityUserProfile(userId: string | undefined) {
  return useQuery<CommunityUserProfile | null>({
    queryKey: communityKeys.userProfile(userId || ''),
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await db
        .from('community_user_profiles')
        .select('user_id, display_name, badge, bio, show_school, is_banned, created_at')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data as CommunityUserProfile | null;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}

/** Fetch a user's recent posts (for profile page) */
export function useUserPosts(userId: string | undefined) {
  return useInfiniteQuery<PostWithContext[]>({
    queryKey: communityKeys.userPosts(userId || ''),
    queryFn: async ({ pageParam }) => {
      if (!userId) return [];
      let query = db
        .from('community_posts')
        .select(POST_WITH_CONTEXT_SELECT)
        .eq('author_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (pageParam) {
        query = query.lt('created_at', pageParam as string);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PostWithContext[];
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1]?.created_at;
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

// ── Search Hook ──────────────────────────────────────────────────────────

/** Search community posts via Postgres full-text search RPC */
export function useCommunitySearch(query: string, spaceSlug?: string) {
  return useQuery<CommunitySearchResult[]>({
    queryKey: communityKeys.search(query, spaceSlug),
    queryFn: async () => {
      const trimmed = query.trim().slice(0, 200);
      if (!trimmed || trimmed.length < 2) return [];
      const { data, error } = await db.rpc('search_community', {
        search_query: trimmed,
        space_slug: spaceSlug || null,
      });
      if (error) throw error;
      return (data ?? []) as CommunitySearchResult[];
    },
    enabled: query.trim().length >= 2,
    staleTime: 60 * 1000,
  });
}

// ── Notification Hooks ──────────────────────────────────────────────────

/** Get count of unread notifications (for bell badge) */
export function useUnreadNotificationCount() {
  const { user } = useAuth();
  return useQuery<number>({
    queryKey: [...communityKeys.notifications(), 'unread-count'],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await db
        .from('user_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

/** Lightweight boolean check for sidebar activity dot (polls every 60s) */
export function useCommunityHasUnread() {
  const { user } = useAuth();
  return useQuery<boolean>({
    queryKey: [...communityKeys.notifications(), 'has-unread'],
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await db.rpc('check_community_unread');
      if (error) throw error;
      return data ?? false;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/** Paginated list of notifications */
export function useNotifications() {
  const { user } = useAuth();
  return useInfiniteQuery<UserNotification[]>({
    queryKey: [...communityKeys.notifications(), 'list'],
    queryFn: async ({ pageParam }) => {
      if (!user) return [];
      let query = db
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (pageParam) {
        query = query.lt('created_at', pageParam as string);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as UserNotification[];
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1]?.created_at;
    },
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

/** Mark a single notification as read (via RPC — prevents column tampering) */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db.rpc('mark_notification_read', {
        notification_id: notificationId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.notifications() });
    },
  });
}

/** Mark all unread notifications as read (via RPC — prevents column tampering) */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await db.rpc('mark_all_notifications_read');
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.notifications() });
    },
  });
}

/** Admin: toggle pin on a post */
export function useTogglePin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, isPinned }: { postId: string; isPinned: boolean }) => {
      const { error } = await db
        .from('community_posts')
        .update({ is_pinned: !isPinned })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.postId) });
      queryClient.invalidateQueries({ queryKey: [...communityKeys.all, 'posts'] });
    },
  });
}

/** Admin: toggle lock on a post */
export function useToggleLock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, isLocked }: { postId: string; isLocked: boolean }) => {
      const { error } = await db
        .from('community_posts')
        .update({ is_locked: !isLocked })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.postId) });
    },
  });
}

// ── Notification Preferences ────────────────────────────────────────────────

const PREF_COLUMNS = 'user_id, email_reply_to_post, email_reply_to_comment, email_mention, email_group_message, email_group_mention, email_session_scheduled, email_support_reply, email_support_status_change, created_at, updated_at';

/** Get user's notification preferences (returns defaults if no row exists) */
export function useNotificationPreferences() {
  const { user } = useAuth();
  return useQuery<NotificationPreferences>({
    queryKey: communityKeys.notificationPrefs(),
    queryFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await db
        .from('notification_preferences')
        .select(PREF_COLUMNS)
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      if (data) return data as NotificationPreferences;
      // Return defaults if no row yet
      return {
        user_id: user.id,
        ...DEFAULT_NOTIFICATION_PREFERENCES,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/** Update a single notification preference toggle */
export function useUpdateNotificationPreference() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: NotificationPrefKey; value: boolean }) => {
      if (!user) throw new Error('Not authenticated');

      // Upsert: insert with defaults if no row, or update the specific column
      const { error } = await db
        .from('notification_preferences')
        .upsert(
          { user_id: user.id, [key]: value },
          { onConflict: 'user_id' },
        );
      if (error) throw error;
    },
    onMutate: async ({ key, value }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: communityKeys.notificationPrefs() });
      const prev = queryClient.getQueryData<NotificationPreferences>(communityKeys.notificationPrefs());
      if (prev) {
        queryClient.setQueryData(communityKeys.notificationPrefs(), { ...prev, [key]: value });
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(communityKeys.notificationPrefs(), context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.notificationPrefs() });
    },
  });
}

// ── Starting Point (Ask JDS AI Response) ─────────────────────────────────

/** Fetch the AI-generated starting point for a post (is_ai = true comment) */
export function useStartingPoint(postId: string | undefined) {
  const { user } = useAuth();
  return useQuery<CommentWithAuthor | null>({
    queryKey: communityKeys.startingPoint(postId ?? ''),
    queryFn: async () => {
      if (!user || !postId) return null;
      // Fetch the AI comment (there should be at most one per post)
      const { data: comments, error: cmtError } = await db
        .from('community_comments')
        .select('id, post_id, parent_comment_id, author_id, body, vote_count, is_ai, depth, edited_at, deleted_at, deleted_by, created_at')
        .eq('post_id', postId)
        .eq('is_ai', true)
        .is('deleted_at', null)
        .limit(1);
      if (cmtError) throw cmtError;
      if (!comments || comments.length === 0) return null;

      const comment = comments[0];
      // Fetch author profile (the AI "user" profile)
      const { data: profiles } = await db
        .from('community_user_profiles')
        .select('user_id, display_name, badge')
        .eq('user_id', comment.author_id);

      const author = profiles?.[0] ?? {
        user_id: comment.author_id,
        display_name: 'Ask JDS',
        badge: null,
      };

      return { ...comment, author } as CommentWithAuthor;
    },
    enabled: !!user && !!postId,
    staleTime: 60 * 1000,
  });
}
